<script lang="ts" setup>
definePageMeta({
  auth: false,
  title: 'Terms of Service | Featrack.io',
  uncheckOnboarding: true,
  unPaidAccess: true,
})
useHead({
  title: 'Terms of service | Featrack.io',
})
</script>

<template>
  <section class="max-w-4xl mx-auto p-6 mt-10">
    <div class="mb-10">
      <NuxtLink to="/">
        <AppButton label="Go Back" variant="ghost" icon="lucide:arrow-left-from-line" />
      </NuxtLink>
    </div>

    <h1 class="text-4xl font-bold mb-4">
      Featrack.io Terms of Service
    </h1>
    <p class="text-sm text-muted-foreground mb-6">
      Last updated: {{ new Date('2024-09-26').toLocaleDateString() }}
    </p>

    <p class="text-lg mb-6 mt-10">
      Welcome to Featrack.io! Please read these Terms of Service ("Terms", "ToS") carefully before using our website or services. By accessing or using Featrack.io ("Service"), you agree to be bound by these Terms. If you do not agree to these Terms, you may not access the Service.
    </p>

    <div class="space-y-8">
      <div>
        <h2 class="text-2xl font-semibold mt-6">
          1. Acceptance of Terms
        </h2>
        <p class="mt-2">
          By creating an account or using any services provided by Featrack.io, you agree to comply with and be bound by these Terms. This agreement applies to all users, including visitors, customers, and others who access the Service.
        </p>
      </div>

      <div>
        <h2 class="text-2xl font-semibold mt-6">
          2. Description of Service
        </h2>
        <p class="mt-2">
          Featrack.io provides tools for businesses to track customer feature usage, monitor consumption, set usage limits, and provide real-time insights. Our Service allows users to manage feature usage and analytics for their customers efficiently.
        </p>
      </div>

      <div>
        <h2 class="text-2xl font-semibold mt-6">
          3. Accounts and Registration
        </h2>
        <ul class="list-disc list-inside mt-2">
          <li>You must register an account by providing accurate and up-to-date information.</li>
          <li>You are responsible for maintaining the confidentiality of your account login credentials and for all activities under your account.</li>
          <li>We reserve the right to suspend or terminate your account for any breach of these Terms.</li>
        </ul>
      </div>

      <div>
        <h2 class="text-2xl font-semibold mt-6">
          4. Subscription and Payment
        </h2>
        <ul class="list-disc list-inside mt-2">
          <li>Featrack.io may offer various subscription plans with different features. Your subscription begins upon successful payment.</li>
          <li>Payment details are required to subscribe to the Service. You authorize us to charge all fees to your payment method.</li>
          <li>Featrack.io reserves the right to modify subscription fees at any time. Users will be notified before any fee changes take effect.</li>
        </ul>
      </div>

      <div>
        <h2 class="text-2xl font-semibold mt-6">
          5. Feature Usage Tracking
        </h2>
        <p class="mt-2">
          Our Service allows you to track your customers' use of features within your application. You are responsible for ensuring that you comply with any applicable data privacy regulations when using Featrack.io to track customer data.
        </p>
      </div>

      <div>
        <h2 class="text-2xl font-semibold mt-6">
          6. Data Collection and Privacy
        </h2>
        <p class="mt-2">
          Featrack.io collects and stores data required to provide the Service. We take data security seriously and use encryption and other security measures to protect your information.
          <br>
          Your use of the Service is also governed by our <NuxtLink to="/privacy" class="text-blue-600 underline">
            Privacy Policy
          </NuxtLink>.
        </p>
      </div>

      <div>
        <h2 class="text-2xl font-semibold mt-6">
          7. License to Use
        </h2>
        <p class="mt-2">
          Featrack.io grants you a limited, non-exclusive, non-transferable license to use the Service for its intended purposes. You may not modify, distribute, or reverse engineer any part of the Service.
        </p>
      </div>

      <div>
        <h2 class="text-2xl font-semibold mt-6">
          8. Prohibited Uses
        </h2>
        <ul class="list-disc list-inside mt-2">
          <li>Use the Service for any illegal purposes.</li>
          <li>Attempt to disrupt the Service or servers.</li>
          <li>Engage in unauthorized access or tampering with the Service.</li>
        </ul>
      </div>

      <div>
        <h2 class="text-2xl font-semibold mt-6">
          9. Termination
        </h2>
        <p class="mt-2">
          We reserve the right to suspend or terminate your access to the Service for any violation of these Terms or for any other reason at our sole discretion. Upon termination, your right to use the Service will immediately cease.
        </p>
      </div>

      <div>
        <h2 class="text-2xl font-semibold mt-6">
          10. Limitation of Liability
        </h2>
        <p class="mt-2">
          Featrack.io and its owners, affiliates, and partners will not be liable for any indirect, incidental, or consequential damages arising from your use of the Service.
        </p>
      </div>

      <div>
        <h2 class="text-2xl font-semibold mt-6">
          11. Changes to the Terms
        </h2>
        <p class="mt-2">
          Featrack.io reserves the right to modify or replace these Terms at any time. Any changes will be posted on this page with a revision date. By continuing to use the Service after changes are posted, you agree to be bound by the updated Terms.
        </p>
      </div>

      <div>
        <h2 class="text-2xl font-semibold mt-6">
          12. Governing Law
        </h2>
        <p class="mt-2">
          These Terms are governed by and construed in accordance with the laws of [Your Country/State], without regard to conflict of law principles.
        </p>
      </div>

      <div>
        <h2 class="text-2xl font-semibold mt-6">
          13. Contact Information
        </h2>
        <p class="mt-2">
          If you have any questions or concerns about these Terms, please contact us at <a href="mailto:contact@featrack.io" class="text-blue-600 underline">support@featrack.io</a>.
        </p>

        <p>
          Featrack.io is operated by Mr MARIETTE JEAN.
        </p>
        <p>N° SIRET : 83072454800011</p>
      </div>
    </div>
  </section>
</template>
